/* biome-ignore-all lint/suspicious/noArrayIndexKey: protocol step position is the stable identity */
import { findById } from '@qi/frequency-db';
import { programForSystem } from '@qi/shared';
import { Button, SegmentedControl, Sheet } from '@qi/ui';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Check, Loader2, Lock, Play } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RebalanceWave } from '../components/rebalance/RebalanceWave.tsx';
import { useResonanceTone } from '../components/scan/useResonanceTone.ts';
import { aggregateBodySystems } from '../lib/body-systems.ts';
import { priorityIdAt } from '../lib/priority.ts';
import {
  buildProtocol,
  buildRebalanceItems,
  nextPriorities,
  type ProtocolMode,
  type RebalanceItem,
  WELLNESS_AUDIO,
} from '../lib/rebalance.ts';
import {
  selectCanRebalance,
  selectPlayedTodayIds,
  useSessionStore,
} from '../stores/session-store.ts';

export interface RebalanceSearch {
  /** Item id (e.g. `wellness:Focus`) cued from a Results-page row button. */
  focus?: string;
  /** Which protocol the user is listening to. */
  mode?: ProtocolMode;
}

export const Route = createFileRoute('/_app/rebalance')({
  validateSearch: (search: Record<string, unknown>): RebalanceSearch => ({
    focus: typeof search.focus === 'string' ? search.focus : undefined,
    mode: search.mode === 'comprehensive' ? 'comprehensive' : 'top3',
  }),
  component: RebalancePage,
});

type PlaybackMode = 'ambient' | 'pulsed-harmonic' | 'binaural';

// How long each frequency inside a multi-frequency item (a RIFE organ program)
// holds before stepping to the next.
const SUBSTEP_MS = 4_000;

// Static fallback when no scan is on file — keeps the screen useful for guest
// visits with no saved protocol yet (mirrors the legacy player fallback).
const FALLBACK_MS = 180_000;

// A recorded qicoil quantum album — the "audio" modality of the default mix.
const FALLBACK_AUDIO: RebalanceItem = {
  id: 'audio:calm-clarity',
  label: 'Calm & Clarity',
  source: 'wellness',
  score: 100,
  // Nominal carrier just so the visualizer breathes; the mp3 is the real sound.
  hz: [432],
  caption: WELLNESS_AUDIO.Calm?.title ?? 'Calm & Clarity',
  durationMs: FALLBACK_MS,
  audioUrl: WELLNESS_AUDIO.Calm?.url ?? '/v1/audio/calm',
};

function toneItem(id: string): RebalanceItem {
  const f = findById(id);
  return {
    id: `resonance:${id}`,
    label: f?.historicalAssociation ?? id,
    source: 'resonance',
    score: 100,
    hz: [f?.frequency ?? 432],
    caption: f ? `${f.frequency} Hz` : id,
    durationMs: FALLBACK_MS,
  };
}

function rifeItem(): RebalanceItem | null {
  const program = programForSystem('Kidney');
  if (!program || program.hz.length === 0) return null;
  return {
    id: `system:${program.organ}`,
    label: program.title,
    source: 'system',
    score: 100,
    hz: program.hz,
    caption: `RIFE · ${program.hz.length} frequencies`,
    durationMs: FALLBACK_MS,
  };
}

// The default no-scan protocol samples all three modalities: a recorded ambient
// track (mp3), a RIFE program, and a solfeggio chord tone. Comprehensive keeps
// the audio + rife lead-in, then walks the full solfeggio ladder.
function buildFallback(mode: ProtocolMode): RebalanceItem[] {
  const rife = rifeItem();
  const lead: RebalanceItem[] = [FALLBACK_AUDIO, ...(rife ? [rife] : [])];
  if (mode === 'comprehensive') {
    const ladder = [
      'sol-174',
      'sol-285',
      'sol-396',
      'sol-417',
      'sol-528',
      'sol-639',
      'sol-741',
      'sol-852',
      'sol-963',
    ];
    return [...lead, ...ladder.map(toneItem)];
  }
  return [...lead, toneItem('sol-528')];
}

function RebalancePage() {
  const { focus, mode } = Route.useSearch();
  const navigate = useNavigate();

  // Playback mode is fixed to ambient now that the mode picker is hidden — the
  // tone still reads it to render the harmonic.
  const [playbackMode] = useState<PlaybackMode>('ambient');
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [endConfirmOpen, setEndConfirmOpen] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const lastTickRef = useRef<number | null>(null);

  const canRebalance = useSessionStore(selectCanRebalance);
  const shareUnlocked = useSessionStore((s) => s.shareUnlocked);
  const rebalanceWindowEndsAt = useSessionStore((s) => s.rebalanceWindowEndsAt);
  const lastScan = useSessionStore((s) => s.lastScan);
  const tongueReading = useSessionStore((s) => s.tongueReading);
  const faceComplexion = useSessionStore((s) => s.faceComplexion);
  const playedTodayIds = useSessionStore(selectPlayedTodayIds);
  const markRebalancePlayed = useSessionStore((s) => s.markRebalancePlayed);

  // The five wellness sub-metrics from the most recent scan.
  const wellnessInputs = useMemo(() => {
    if (!lastScan) return [];
    const s = lastScan.scores;
    return [
      { label: 'Focus', value: s.focus },
      { label: 'Energy', value: s.energy },
      { label: 'Recovery', value: s.recovery },
      { label: 'Mood', value: s.emotionalCoherence },
      { label: 'Calm', value: 100 - s.stressLoad },
    ];
  }, [lastScan]);

  // Body systems pooled from the face + tongue zone reads.
  const systems = useMemo(
    () => aggregateBodySystems(faceComplexion?.regions ?? [], tongueReading?.regions ?? []),
    [faceComplexion, tongueReading],
  );

  // Pool every actionable item from the most recent scan: weak wellness
  // sub-metrics, weak body systems, and the strongest resonance peaks.
  const items = useMemo(() => {
    if (!lastScan) return [];
    return buildRebalanceItems({
      wellness: wellnessInputs,
      systems,
      peakFrequencyIds: lastScan.peakFrequencyIds,
    });
  }, [lastScan, wellnessInputs, systems]);

  // The free/share play ladder, on the same pooled ranking as the Results-page
  // priority badges: priority 1 plays free, priority 2 unlocks once the user
  // shares. Both ids stay playable when the paid rebalancing window is locked.
  const priorityRows = useMemo(
    () => [
      ...wellnessInputs.map((m) => ({ id: `wellness:${m.label}`, score: m.value })),
      ...systems.map((s) => ({ id: `system:${s.system}`, score: s.score })),
    ],
    [wellnessInputs, systems],
  );
  const freeId = useMemo(() => priorityIdAt(priorityRows, 0), [priorityRows]);
  const shareId = useMemo(() => priorityIdAt(priorityRows, 1), [priorityRows]);

  // Ids a free (un-paid) user may play: priority 1 always, priority 2 once shared.
  const freePlayable = useMemo(
    () => (id: string) => id === freeId || (shareUnlocked && id === shareId),
    [freeId, shareId, shareUnlocked],
  );

  // A row-cued `focus` plays that one track only (resolved from the deficit
  // pool, or synthesized for a resonance peak that isn't a deficit). Without a
  // focus, a paid user keeps the multi-track Top-3 / Comprehensive protocols —
  // but a free user only ever gets their one free frequency, so the screen
  // collapses to that single track for them.
  const single = useMemo(() => {
    if (focus) {
      const inPool = items.find((i) => i.id === focus);
      if (inPool) return inPool;
      if (focus.startsWith('resonance:')) {
        const id = focus.slice('resonance:'.length);
        if (findById(id)) return toneItem(id);
      }
      return null;
    }
    // No explicit focus: free users are limited to a single free frequency, so
    // pin the screen to that one track rather than a full protocol.
    if (!canRebalance && freeId) return items.find((i) => i.id === freeId) ?? null;
    return null;
  }, [focus, items, canRebalance, freeId]);

  // A locked free user can still play their one free pick — but only when the
  // cued single track *is* that pick. Everything else stays gated.
  const canPlayThis = canRebalance || (single != null && freePlayable(single.id));

  const { protocol, isFallback } = useMemo(() => {
    if (single) return { protocol: [single], isFallback: false };
    const built = buildProtocol(items, mode ?? 'top3', focus);
    return built.length > 0
      ? { protocol: built, isFallback: false }
      : { protocol: buildFallback(mode ?? 'top3'), isFallback: true };
  }, [single, items, mode, focus]);

  // After the single track, the next most-in-need deficits — the just-played
  // one is excluded, and any finished today are flagged.
  const priorities = useMemo(
    () => (single ? nextPriorities(items, single.id, 3) : []),
    [single, items],
  );

  const totalMs = useMemo(() => protocol.reduce((sum, i) => sum + i.durationMs, 0), [protocol]);

  const current = currentItem(protocol, elapsed);
  const progress = Math.min(1, elapsed / totalMs);
  const remaining = totalMs - elapsed;

  // The status line never claims "Now playing" while paused/idle: it reads the
  // real transport state so a stopped timer says so.
  const trackPos = single || !current ? '' : ` · ${current.index + 1} of ${protocol.length}`;
  const statusLabel = playing
    ? `Now playing${trackPos}`
    : elapsed > 0
      ? `Paused${trackPos}`
      : 'Ready to play';

  // Mark a single track "played today" once its full duration elapses. The
  // up-next priorities list reads this back to flag what's already done.
  useEffect(() => {
    if (single && totalMs > 0 && elapsed >= totalMs) markRebalancePlayed(single.id);
  }, [single, elapsed, totalMs, markRebalancePlayed]);

  // Within a multi-frequency item, cycle through its frequencies on a sub-step.
  const currentHz = useMemo(() => {
    if (!current) return 432;
    const into = elapsed - current.startedAt;
    const sub = Math.floor(into / SUBSTEP_MS) % current.item.hz.length;
    return current.item.hz[sub] ?? current.item.hz[0] ?? 432;
  }, [current, elapsed]);

  // An mp3 step plays a recorded track; otherwise the tone is synthesized live.
  const audioUrl = current?.item.audioUrl;
  const isAudioStep = !!audioUrl;

  // Hold the elapsed clock while a recorded track is still buffering, so the
  // protocol can't run ahead of silent (not-yet-loaded) audio. A ref keeps the
  // tick effect from re-subscribing on every buffering flip.
  const holdClockRef = useRef(false);
  holdClockRef.current = isAudioStep && buffering;

  useEffect(() => {
    if (!playing) {
      lastTickRef.current = null;
      return;
    }
    const id = window.setInterval(() => {
      // Backgrounded tabs: drop the anchor and skip the tick so idle wall-clock
      // time doesn't burn the protocol.
      if (document.hidden) {
        lastTickRef.current = null;
        return;
      }
      const now = performance.now();
      // While buffering, keep the anchor fresh but don't advance — the clock
      // resumes seamlessly once the track is playing.
      if (holdClockRef.current) {
        lastTickRef.current = now;
        return;
      }
      const delta = lastTickRef.current ? now - lastTickRef.current : 0;
      lastTickRef.current = now;
      setElapsed((prev) => Math.min(totalMs, prev + delta));
    }, 200);
    return () => window.clearInterval(id);
  }, [playing, totalMs]);

  const tone = useResonanceTone(currentHz, 0.1, 'fifth', playbackMode);
  useEffect(() => {
    // Synthesize only on tone steps — silence the oscillator under an mp3.
    const wantSynth = playing && !isAudioStep;
    if (wantSynth && !tone.playing) void tone.start();
    if (!wantSynth && tone.playing) tone.stop();
  }, [playing, isAudioStep, tone]);

  // Preload the recorded track the moment its URL is known — before Begin — so
  // buffering happens up front instead of as a silent delay after pressing play.
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    if (!audioUrl) return;
    const el = audioElRef.current;
    if (el?.src.endsWith(audioUrl)) return;
    el?.pause();
    const next = new Audio(audioUrl);
    next.loop = true;
    next.volume = 0.7;
    next.preload = 'auto';
    next.load();
    audioElRef.current = next;
  }, [audioUrl]);

  // Play/pause the preloaded element and track buffering so the clock can wait
  // on it. `waiting`/`playing` events flip the spinner; readyState seeds the
  // initial state in case the track isn't buffered far enough yet.
  useEffect(() => {
    const el = audioElRef.current;
    if (!el || !audioUrl) {
      setBuffering(false);
      return;
    }
    if (!playing) {
      el.pause();
      setBuffering(false);
      return;
    }
    const onWaiting = () => setBuffering(true);
    const onPlaying = () => setBuffering(false);
    el.addEventListener('waiting', onWaiting);
    el.addEventListener('playing', onPlaying);
    // HAVE_FUTURE_DATA (3) or better means it can play without stalling.
    setBuffering(el.readyState < 3);
    void el.play().catch(() => {});
    return () => {
      el.removeEventListener('waiting', onWaiting);
      el.removeEventListener('playing', onPlaying);
    };
  }, [playing, audioUrl]);

  // Stop any recorded track on unmount so leaving the route silences it.
  useEffect(() => {
    return () => {
      audioElRef.current?.pause();
      audioElRef.current = null;
    };
  }, []);

  const setMode = (next: ProtocolMode) => {
    setElapsed(0);
    setPlaying(false);
    navigate({ to: '/rebalance', search: { focus, mode: next }, replace: true });
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-6 py-8">
      <header className="text-center">
        <h1 className="font-display text-3xl text-ink-0">Rebalancing Protocol</h1>
      </header>

      {/* Protocol chooser — the daily plan vs the full pass. Hidden for a
          single row-cued track, which plays one tone, not a protocol. */}
      {!single && (
        <div className="flex justify-center">
          <SegmentedControl
            ariaLabel="Protocol"
            value={mode ?? 'top3'}
            onChange={setMode}
            options={[
              { value: 'top3', label: 'Top 3' },
              { value: 'comprehensive', label: 'Comprehensive' },
            ]}
          />
        </div>
      )}

      {!canRebalance && canPlayThis && (
        <div className="mx-auto flex max-w-md flex-col items-center gap-2 rounded-lg border border-aurora/30 bg-surface-2 px-6 py-4 text-center">
          <p className="text-sm text-ink-1">
            This is your free track — your most in-need metric. Unlock to play your full protocol
            and every other tone.
          </p>
          <Link
            to="/unlock"
            className="rounded-sm border border-aurora/50 bg-surface-1 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-aurora transition-colors hover:border-aurora/80"
          >
            Unlock everything
          </Link>
        </div>
      )}

      {!canPlayThis && (
        <div className="mx-auto flex max-w-md flex-col items-center gap-3 rounded-lg border border-violet/40 bg-surface-2 px-6 py-5 text-center">
          <Lock size={18} strokeWidth={1.8} className="text-violet" />
          <p className="text-sm text-ink-1">
            {rebalanceWindowEndsAt
              ? 'Your 7-day rebalancing window has ended. Your reading stays yours — subscribe to keep playing your tones.'
              : 'Unlock your reading to play your rebalancing tones. Keep the full reading forever, plus 7 days of rebalancing.'}
          </p>
          <Link
            to="/unlock"
            className="rounded-sm border border-violet/50 bg-surface-1 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-violet transition-colors hover:border-violet/80"
          >
            {rebalanceWindowEndsAt ? 'Subscribe to continue' : 'Unlock rebalancing'}
          </Link>
        </div>
      )}

      <section
        className="rounded-lg border border-hairline bg-surface-1 p-6 md:p-8"
        style={{ boxShadow: 'var(--lift-1)' }}
      >
        <div className="mx-auto flex max-w-md flex-col items-center gap-5 text-center">
          {current && (
            <div className="w-full space-y-1.5">
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-aurora">
                {statusLabel}
              </p>
              <p className="font-display text-2xl leading-tight text-ink-0">
                {current.item.label} Frequency
              </p>
              {playing && isAudioStep && buffering && (
                <p className="flex items-center justify-center gap-2 pt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-aurora">
                  <Loader2 size={13} className="animate-spin" aria-hidden="true" />
                  Loading track…
                </p>
              )}
            </div>
          )}

          {/* Timer box: the animated wave sits behind the countdown, and the
              progress bar rides the container's bottom border. */}
          <div className="relative w-full overflow-hidden rounded-lg border border-hairline bg-surface-2">
            <div className="pointer-events-none absolute inset-0 opacity-60">
              <RebalanceWave playing={playing} hz={currentHz} className="h-full w-full" />
            </div>
            <div className="relative flex flex-col items-center gap-1 px-6 py-12">
              <span className="font-mono text-6xl tracking-tight text-ink-0 tabular-nums">
                {formatMs(remaining)}
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
                Remaining
              </span>
            </div>
            <div className="absolute inset-x-0 bottom-0 h-1 overflow-hidden bg-surface-3">
              <div
                className="h-full transition-[width] duration-200"
                style={{
                  width: `${progress * 100}%`,
                  background: 'linear-gradient(90deg, var(--violet), var(--aurora))',
                  boxShadow: '0 0 10px color-mix(in oklab, var(--aurora) 50%, transparent)',
                }}
              />
              {protocol.slice(1).map((_, i) => {
                const segmentEnd =
                  protocol.slice(0, i + 1).reduce((sum, p) => sum + p.durationMs, 0) / totalMs;
                return (
                  <span
                    key={i}
                    aria-hidden="true"
                    className="absolute top-0 h-full w-px bg-abyss"
                    style={{ left: `${segmentEnd * 100}%` }}
                  />
                );
              })}
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="resonance"
              size="lg"
              disabled={!canPlayThis}
              onClick={() => setPlaying((p) => !p)}
              aria-pressed={playing}
            >
              {playing ? 'Pause' : 'Start Rebalance'}
            </Button>
          </div>
        </div>
      </section>

      <Sheet
        open={endConfirmOpen}
        onClose={() => setEndConfirmOpen(false)}
        title="End this session?"
        description="Your in-progress timer will be discarded."
      >
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <Button variant="ghost" onClick={() => setEndConfirmOpen(false)}>
            Keep going
          </Button>
          <Button
            variant="resonance"
            data-primary-action="true"
            onClick={() => {
              setEndConfirmOpen(false);
              // Count a single track as done if most of it was heard.
              if (single && totalMs > 0 && elapsed / totalMs >= 0.9) {
                markRebalancePlayed(single.id);
              }
              navigate({ to: '/compare' });
            }}
          >
            End session
          </Button>
        </div>
      </Sheet>

      {/* Up next today — only when a single track is cued. Lists the next
          most-in-need priorities; ones finished today are flagged so the user
          knows what's left. Tapping one re-cues it as its own single track. */}
      {single && (
        <section className="space-y-3">
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink-2">Up next today</p>
          {priorities.length === 0 ? (
            <p className="text-sm text-ink-2">
              That's your whole priority list for today — nice work.
            </p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-hairline bg-surface-1">
              {priorities.map((item) => {
                const played = playedTodayIds.includes(item.id);
                // A free user gets one real track (their free pick); every other
                // priority here is locked behind the unlock.
                const locked = !canRebalance && !freePlayable(item.id);
                if (locked) {
                  return (
                    <Link
                      key={item.id}
                      to="/unlock"
                      className="flex w-full items-center justify-between gap-4 border-t border-hairline px-6 py-4 text-left transition-colors first:border-t-0 hover:bg-[color-mix(in_oklab,var(--violet)_7%,transparent)]"
                    >
                      <div>
                        <p className="text-sm text-ink-2">{item.label}</p>
                        <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink-3">
                          {item.caption}
                        </p>
                      </div>
                      <span className="flex shrink-0 items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-violet">
                        <Lock size={12} strokeWidth={1.8} aria-hidden="true" />
                        Unlock
                      </span>
                    </Link>
                  );
                }
                return (
                  <Link
                    key={item.id}
                    to="/rebalance"
                    search={{ focus: item.id, mode: 'top3' }}
                    className="flex w-full items-center justify-between gap-4 border-t border-hairline px-6 py-4 text-left transition-colors first:border-t-0 hover:bg-[color-mix(in_oklab,var(--aurora)_7%,transparent)]"
                  >
                    <div>
                      <p className="text-sm text-ink-1">{item.label}</p>
                      <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink-3">
                        {item.caption}
                      </p>
                    </div>
                    {played ? (
                      <span className="flex shrink-0 items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-jade">
                        <Check size={13} strokeWidth={2.2} aria-hidden="true" />
                        Played today
                      </span>
                    ) : (
                      <span className="flex shrink-0 items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-aurora">
                        <Play size={12} strokeWidth={1.8} aria-hidden="true" />
                        Start
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* Protocol roadmap — multi-track protocols only. */}
      {!single && (
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
              {isFallback ? 'Generic starter protocol' : 'Protocol'}
            </p>
            {isFallback && (
              <Link
                to="/scan/bioscan"
                className="font-mono text-[11px] uppercase tracking-[0.14em] text-aurora hover:text-aurora/80"
              >
                Scan to personalize
              </Link>
            )}
          </div>
          {isFallback && (
            <p className="text-sm text-ink-2">
              These are general wellness tones, not tuned to you. Run a scan to build a protocol
              from your own resonance.
            </p>
          )}
          <div className="overflow-hidden rounded-lg border border-hairline bg-surface-1">
            {protocol.map((item, idx) => {
              const active = current?.index === idx;
              // Cumulative start offset of this step — seeking here restarts the
              // frequency from its beginning.
              const startAt = protocol.slice(0, idx).reduce((sum, p) => sum + p.durationMs, 0);
              return (
                <button
                  type="button"
                  key={item.id + idx}
                  disabled={!canRebalance}
                  aria-current={active ? 'true' : undefined}
                  onClick={() => {
                    setElapsed(startAt);
                    setPlaying(true);
                  }}
                  className={`flex w-full items-center justify-between gap-4 border-t border-hairline px-6 py-4 text-left transition-colors first:border-t-0 enabled:hover:bg-[color-mix(in_oklab,var(--aurora)_7%,transparent)] disabled:cursor-not-allowed ${
                    active ? 'bg-[color-mix(in_oklab,var(--aurora)_4%,transparent)]' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span
                      className={`font-mono text-xs uppercase tracking-[0.18em] ${active ? 'text-aurora' : 'text-ink-3'}`}
                    >
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <p className={`text-sm ${active ? 'text-ink-0' : 'text-ink-1'}`}>
                        {item.label}
                      </p>
                      <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink-3">
                        {item.caption}
                      </p>
                    </div>
                  </div>
                  <span className="font-mono text-xs text-ink-2 tabular-nums">
                    {formatMs(item.durationMs)}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <div className="flex justify-center pt-2">
        <Button
          variant="ghost"
          onClick={() => {
            if (elapsed === 0 && !playing) {
              navigate({ to: '/compare' });
              return;
            }
            setPlaying(false);
            setEndConfirmOpen(true);
          }}
        >
          End session
        </Button>
      </div>
    </div>
  );
}

interface CurrentItem {
  index: number;
  item: RebalanceItem;
  startedAt: number;
}

function currentItem(protocol: RebalanceItem[], elapsedMs: number): CurrentItem | null {
  let cursor = 0;
  for (let i = 0; i < protocol.length; i++) {
    const item = protocol[i];
    if (!item) continue;
    const end = cursor + item.durationMs;
    if (elapsedMs < end) return { index: i, item, startedAt: cursor };
    cursor = end;
  }
  const last = protocol[protocol.length - 1];
  return last
    ? { index: protocol.length - 1, item: last, startedAt: cursor - last.durationMs }
    : null;
}

function formatMs(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
